import { useState, useCallback, useEffect } from 'react'
import { supabase } from '@/lib/supabase/client'
import {
  STMIDBEquipmentService,
  isSTMIDBCompany,
  mapSTMIDBToWLLEquipment,
  resolveWLLCategoryIds,
  mapSTMIDBToWLLCategoryCode,
} from '@/services/api/stmidbEquipmentService'

interface Equipment {
  id: string
  company_id: string
  name: string
  part_number: string | null
  asset_number: string
  serial_number: string | null
  owner_id?: string | null
  last_inspection_date: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  next_inspection_date: string | null
  status: string
  inspection_status?: string | null
  description: string | null
  model: string | null
  manufacture_year: number | null
  category_id: string | null
  created_at: string
}

// Supabase's API caps every response at 1000 rows, so the list is paged.
const STMIDB_PAGE_SIZE = 1000

const fetchSTMIDBEquipmentList = async (): Promise<Equipment[]> => {
  const rows: Awaited<ReturnType<typeof STMIDBEquipmentService.getEquipment>> = []
  for (let offset = 0; ; offset += STMIDB_PAGE_SIZE) {
    const page = await STMIDBEquipmentService.getEquipment({ limit: STMIDB_PAGE_SIZE, offset })
    rows.push(...page)
    if (page.length < STMIDB_PAGE_SIZE) break
  }
  const categoryIds = await resolveWLLCategoryIds(
    rows.map(row => mapSTMIDBToWLLCategoryCode(row.category, row.equipment_type)),
  )
  return rows
    .map(row => mapSTMIDBToWLLEquipment(row, categoryIds))
    .sort((a, b) => (b.last_inspection_date || '').localeCompare(a.last_inspection_date || ''))
}

export const fetchSTMIDBEquipmentById = async (equipmentId: string) => {
  const row = await STMIDBEquipmentService.getEquipmentById(equipmentId)
  if (!row) return null
  const categoryIds = await resolveWLLCategoryIds([
    mapSTMIDBToWLLCategoryCode(row.category, row.equipment_type),
  ])
  return mapSTMIDBToWLLEquipment(row, categoryIds)
}

export const useEquipment = (companyId?: string) => {
  const [equipment, setEquipment] = useState<Equipment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchEquipment = useCallback(async () => {
    if (!companyId) return

    setLoading(true)
    setError(null)

    try {
      if (isSTMIDBCompany(companyId)) {
        setEquipment(await fetchSTMIDBEquipmentList())
        return
      }

      const { data, error: fetchError } = await supabase
        .from('equipment_v2')
        .select('*')
        .eq('company_id', companyId)
        .order('last_inspection_date', { ascending: false, nullsFirst: false })

      if (fetchError) throw fetchError

      setEquipment(data || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch equipment'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [companyId])

  const getEquipmentById = useCallback(async (equipmentId: string) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('equipment_v2')
        .select('*')
        .eq('id', equipmentId)
        .maybeSingle()

      if (fetchError) throw fetchError
      if (data) return data

      const stmidb = await fetchSTMIDBEquipmentById(equipmentId)
      if (!stmidb) throw new Error('Equipment not found')
      return stmidb
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch equipment'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEquipment()
  }, [fetchEquipment])

  return {
    equipment,
    loading,
    error,
    refetch: fetchEquipment,
    getEquipmentById,
  }
}
